import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import CloseIcon from '@material-ui/icons/Close';
import FolderIcon from '@material-ui/icons/Folder';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import VideoLibraryIcon from '@material-ui/icons/VideoLibrary';
import ImageIcon from '@material-ui/icons/Image';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import HomeIcon from '@material-ui/icons/Home';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import CircularProgress from '@material-ui/core/CircularProgress';
import styles from './DropboxFileBrowser.module.css';

const getFileIcon = (mimeType, isFolder) => {
  if (isFolder) {
    return <FolderIcon />;
  }

  if (mimeType.startsWith('video/')) {
    return <VideoLibraryIcon />;
  }
  if (mimeType.startsWith('image/')) {
    return <ImageIcon />;
  }
  return <InsertDriveFileIcon />;
};

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '';

  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${Math.round((bytes / (1024 ** i)) * 100) / 100} ${sizes[i]}`;
};

export const GoogleDriveFileBrowser = ({ accessToken, onClose, onFilesSelected, onShowMessage, onDisconnect, onTokenExpired }) => {
  const [currentFolderId, setCurrentFolderId] = useState('root');
  const [folderHistory, setFolderHistory] = useState([{ id: 'root', name: 'My Drive' }]);
  const [items, setItems] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingFolderId, setLoadingFolderId] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) {
      return items;
    }
    const query = searchQuery.toLowerCase().trim();
    return items.filter((item) => item.name.toLowerCase().includes(query));
  }, [items, searchQuery]);

  // Function to recursively get all video files from a folder
  const getAllVideosFromFolder = async (folderId, folderName = '') => {
    try {
      // First, get all items (folders and videos) in the current folder
      const query = `'${folderId}' in parents and trashed=false`;

      const response = await axios.get(
        'https://www.googleapis.com/drive/v3/files',
        {
          params: {
            q: query,
            fields: 'files(id, name, mimeType, size, webContentLink)',
            pageSize: 1000,
          },
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      const items = response.data.files || [];
      const videoFiles = [];
      const subfolders = [];

      // Separate videos and subfolders
      items.forEach((item) => {
        if (item.mimeType === 'application/vnd.google-apps.folder') {
          subfolders.push(item);
        } else if (item.mimeType.startsWith('video/')) {
          videoFiles.push({
            id: item.id,
            name: item.name,
            size: parseInt(item.size, 10) || 0,
            mimeType: item.mimeType,
            webContentLink: item.webContentLink,
          });
        }
      });

      // Recursively get videos from subfolders in parallel
      if (subfolders.length > 0) {
        const subfolderPromises = subfolders.map((subfolder) => getAllVideosFromFolder(subfolder.id, subfolder.name));
        const subfolderResults = await Promise.all(subfolderPromises);
        subfolderResults.forEach((subfolderVideos) => {
          videoFiles.push(...subfolderVideos);
        });
      }

      return videoFiles;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error loading videos from folder:', err);
      return [];
    }
  };

  const loadFolder = async (folderId) => {
    setIsLoading(true);
    setError(null);

    try {
      // Query to get folders and videos only (images commented out)
      // const query = `'${folderId}' in parents and trashed=false and (mimeType='application/vnd.google-apps.folder' or mimeType contains 'video/' or mimeType contains 'image/')`;
      const query = `'${folderId}' in parents and trashed=false and (mimeType='application/vnd.google-apps.folder' or mimeType contains 'video/')`;

      const response = await axios.get(
        'https://www.googleapis.com/drive/v3/files',
        {
          params: {
            q: query,
            fields: 'files(id, name, mimeType, size, thumbnailLink, webContentLink)',
            orderBy: 'folder,name',
            pageSize: 1000,
          },
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      setItems(response.data.files || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error loading Google Drive folder:', err);

      // Check if token expired (401 Unauthorized)
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        if (onTokenExpired) {
          onTokenExpired();
          return;
        }
      }

      setError('Failed to load folder contents');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) {
      loadFolder(currentFolderId);
    }
    // eslint-disable-next-line
  }, [currentFolderId, accessToken]);

  const handleFolderClick = (folder) => {
    setFolderHistory([...folderHistory, { id: folder.id, name: folder.name }]);
    setCurrentFolderId(folder.id);
    setSearchQuery(''); // Clear search when navigating
  };

  const handleBackClick = () => {
    if (folderHistory.length > 1) {
      const newHistory = [...folderHistory];
      newHistory.pop();
      const previousFolder = newHistory[newHistory.length - 1];
      setFolderHistory(newHistory);
      setCurrentFolderId(previousFolder.id);
      setSearchQuery(''); // Clear search when navigating back
    }
  };

  const handleHomeClick = () => {
    setFolderHistory([{ id: 'root', name: 'My Drive' }]);
    setCurrentFolderId('root');
    setSearchQuery(''); // Clear search when going home
  };

  const handleFileToggle = (file) => {
    const fileId = file.id;
    const isSelected = selectedFiles.some((f) => f.id === fileId);

    if (isSelected) {
      setSelectedFiles(selectedFiles.filter((f) => f.id !== fileId));
    } else {
      setSelectedFiles([...selectedFiles, {
        id: fileId,
        name: file.name,
        size: parseInt(file.size, 10) || 0,
        mimeType: file.mimeType,
        webContentLink: file.webContentLink,
      }]);
    }
  };

  const handleFolderToggle = async (folder) => {
    const folderId = folder.id;
    const isSelected = selectedFolders.some((f) => f.id === folderId);

    if (isSelected) {
      // Deselect folder and remove all its files
      setSelectedFolders(selectedFolders.filter((f) => f.id !== folderId));
      const folderData = selectedFolders.find((f) => f.id === folderId);
      if (folderData && folderData.files) {
        // Remove all files that belong to this folder
        const folderFileIds = folderData.files.map((file) => file.id);
        setSelectedFiles(selectedFiles.filter((f) => !folderFileIds.includes(f.id)));
      }
    } else {
      // Select folder and get all its video files
      setLoadingFolderId(folderId);
      const videoFiles = await getAllVideosFromFolder(folder.id, folder.name);
      setLoadingFolderId(null);

      if (videoFiles.length === 0) {
        // Show toast if no videos found
        if (onShowMessage) {
          onShowMessage('Nothing to select');
        }
      } else {
        // Mark each file with the folder name for category tracking
        const filesWithFolderName = videoFiles.map((file) => ({
          ...file,
          folder_name: folder.name,
        }));

        // Add folder to selected folders
        setSelectedFolders([...selectedFolders, {
          id: folderId,
          name: folder.name,
          files: filesWithFolderName,
        }]);

        // Add all video files to selected files (avoid duplicates)
        const existingFileIds = selectedFiles.map((f) => f.id);
        const newFiles = filesWithFolderName.filter((file) => !existingFileIds.includes(file.id));
        setSelectedFiles([...selectedFiles, ...newFiles]);
      }
    }
  };

  const handleImport = () => {
    if (selectedFiles.length > 0) {
      onFilesSelected(selectedFiles);
    }
  };

  const isFileSelected = (fileId) => selectedFiles.some((f) => f.id === fileId);
  const isFolderSelected = (folderId) => selectedFolders.some((f) => f.id === folderId);

  const handleClearSelection = () => {
    setSelectedFiles([]);
    setSelectedFolders([]);
  };

  const getCurrentFolderName = () => folderHistory[folderHistory.length - 1].name;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const isFolder = (item) => item.mimeType === 'application/vnd.google-apps.folder';
  const isFile = (item) => !isFolder(item);

  const handleSelectAll = () => {
    // Select all files in current folder
    const allFiles = items.filter((item) => isFile(item)).map((file) => ({
      id: file.id,
      name: file.name,
      size: parseInt(file.size, 10) || 0,
      mimeType: file.mimeType,
      webContentLink: file.webContentLink,
    }));
    setSelectedFiles(allFiles);
  };

  return (
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.breadcrumb}>
              <button
                className={styles.breadcrumbButton}
                onClick={handleHomeClick}
                disabled={currentFolderId === 'root'}
              >
                <HomeIcon style={{ fontSize: 16 }} />
              </button>
              {folderHistory.length > 1 && (
                <>
                  <span className={styles.breadcrumbSeparator}>/</span>
                  <span className={styles.breadcrumbCurrent}>{getCurrentFolderName()}</span>
                </>
              )}
            </div>
            {folderHistory.length === 1 && <h2 className={styles.title}>Browse Google Drive</h2>}

          </div>
          <div className={styles.searchContainer}>
            <SearchIcon className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search folders and files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className={styles.searchClearButton}
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                <ClearIcon style={{ fontSize: 18 }} />
              </button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {onDisconnect && (
              <button
                className={styles.disconnectButton}
                onClick={onDisconnect}
                title="Switch Account"
              >
                Switch Account
              </button>
            )}
            <button className={styles.closeButton} onClick={onClose}>
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Navigation */}
        {folderHistory.length > 1 && (
          <div className={styles.navigation}>
            <button className={styles.backButton} onClick={handleBackClick}>
              <ArrowBackIcon style={{ marginRight: 8 }} />
              Back
            </button>
          </div>
        )}

        {/* Content */}
        <div className={styles.content}>
          {isLoading && (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
              <p>Loading...</p>
            </div>
          )}

          {error && (
            <div className={styles.errorState}>
              <p>{error}</p>
              <button
                className={styles.retryButton}
                onClick={() => loadFolder(currentFolderId)}
              >
                Retry
              </button>
            </div>
          )}

          {!isLoading && !error && items.length === 0 && (
            <div className={styles.emptyState}>
              <FolderIcon style={{ fontSize: 64, color: '#ccc' }} />
              <p>This folder is empty</p>
            </div>
          )}

          {!isLoading && !error && items.length > 0 && filteredItems.length === 0 && (
            <div className={styles.emptyState}>
              <SearchIcon style={{ fontSize: 64, color: '#ccc' }} />
              <p>
                No results found for &ldquo;
                {searchQuery}
                &rdquo;
              </p>
            </div>
          )}

          {!isLoading && !error && filteredItems.length > 0 && (
            <div className={styles.itemList}>
              {filteredItems.map((item) => {
                const itemIsFolder = isFolder(item);
                const itemIsFile = isFile(item);
                const isSelected = itemIsFile ? isFileSelected(item.id) : isFolderSelected(item.id);

                return (
                  <div
                    key={item.id}
                    className={`${styles.item} ${itemIsFolder ? styles.itemFolder : ''} ${isSelected ? styles.itemSelected : ''}`}
                    onClick={() => {
                      if (itemIsFolder) {
                        handleFolderClick(item);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && itemIsFolder) {
                        handleFolderClick(item);
                      }
                    }}
                  >
                    <div className={styles.itemIcon}>
                      {getFileIcon(item.mimeType, itemIsFolder)}
                    </div>

                    <div className={styles.itemInfo}>
                      <div className={styles.itemName}>{item.name}</div>
                      {itemIsFile && item.size && (
                        <div className={styles.itemSize}>{formatFileSize(item.size)}</div>
                      )}
                    </div>

                    {itemIsFile && (
                      <button
                        className={styles.selectButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFileToggle(item);
                        }}
                      >
                        {isSelected ? (
                          <CheckBoxIcon style={{ color: '#667eea' }} />
                        ) : (
                          <CheckBoxOutlineBlankIcon />
                        )}
                      </button>
                    )}

                    {itemIsFolder && (
                      <button
                        className={styles.selectButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFolderToggle(item);
                        }}
                        disabled={loadingFolderId === item.id}
                      >
                        {loadingFolderId === item.id ? (
                          <CircularProgress size={20} style={{ color: '#667eea' }} />
                        ) : isSelected ? (
                          <CheckBoxIcon style={{ color: '#667eea' }} />
                        ) : (
                          <CheckBoxOutlineBlankIcon />
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <div className={styles.footerInfo}>
            {selectedFiles.length > 0 && (
              <span className={styles.selectedCount}>
                {selectedFiles.length}
                {' '}
                file
                {selectedFiles.length !== 1 ? 's' : ''}
                {' '}
                selected
              </span>
            )}
          </div>
          <div className={styles.footerActions}>
            <button className={styles.cancelButton} onClick={onClose}>
              Cancel
            </button>
            {selectedFiles.length < items.filter((item) => isFile(item)).length && (
              <button className={styles.cancelButton} onClick={handleSelectAll}>
                Select All
              </button>
            )}
            {selectedFiles.length > 0 && (
              <button className={styles.cancelButton} onClick={handleClearSelection}>
                Clear Selection
              </button>
            )}
            <button
              className={styles.importButton}
              onClick={handleImport}
              disabled={selectedFiles.length === 0}
            >
              Import
              {' '}
              {selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
